/**
 * Requirements Matrix Page
 *
 * Shows a matrix of document types × shipment statuses with requirement levels.
 * Cells are clickable to create/edit requirements.
 * Supports adding new doc type rows and schema-level overrides.
 */

import { useState, useMemo } from 'react';
import { AlertCircle, Plus, Trash01 } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import type { SelectItemType } from '@/components/base/select/select';
import {
    useRequirementsMatrix,
    useConditionPresets,
    useCreateRequirement,
    useUpdateRequirement,
    useDeleteRequirement,
    useDeleteAllRequirements,
} from '@/hooks/api/use-document-requirements';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import type {
    RequirementLevel,
    MatrixStatusEntry,
    ConditionGroup,
    ConditionPreset,
} from '@/types/api/document-requirements';

// ============================================================================
// Constants
// ============================================================================

const STATUS_LABELS: Record<string, string> = {
    draft: 'Draft',
    booked: 'Booked',
    in_transit: 'In Transit',
    on_hold: 'On Hold',
    arrived: 'Arrived',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
};

// UI levels — REQUIRED and EXPECTED are unified as "Expected" with a delay field
// delay=0 → immediately required (backend: 'required'), delay>0 → escalates after X hours (backend: 'expected')
const LEVEL_OPTIONS: SelectItemType[] = [
    { id: 'expected', label: 'Expected (escalates to Required after delay)' },
    { id: 'required_if', label: 'Required If (conditional, can escalate)' },
    { id: 'optional', label: 'Optional (no action)' },
];

type CellColor = 'error' | 'warning' | 'brand' | 'gray';

const LEVEL_CONFIG: Record<string, { label: string; color: CellColor; short: string }> = {
    required: { label: 'Expected (0h)', color: 'error', short: 'EXP' },
    required_if: { label: 'Required If', color: 'warning', short: 'REQ IF' },
    expected: { label: 'Expected', color: 'brand', short: 'EXP' },
    optional: { label: 'Optional', color: 'gray', short: 'OPT' },
};

// ============================================================================
// Cell Component
// ============================================================================

function MatrixCell({
    entry,
    onClick,
}: {
    entry: MatrixStatusEntry | undefined;
    onClick: () => void;
}) {
    if (!entry) {
        return (
            <td
                className="px-2 py-2 text-center cursor-pointer hover:bg-tertiary/50 transition-colors border border-secondary"
                onClick={onClick}
            >
                <span className="text-xs text-quaternary">—</span>
            </td>
        );
    }

    const config = LEVEL_CONFIG[entry.requirement_level] || LEVEL_CONFIG.optional;
    const hasConditions = entry.conditions && entry.conditions.rules?.length > 0;
    const hasEscalation = entry.escalation_delay_hours != null && entry.escalation_delay_hours > 0;

    // For the unified expected/required model: show delay context
    const delayLabel =
        entry.requirement_level === 'required'
            ? '0h'
            : entry.escalation_delay_hours != null
              ? `${entry.escalation_delay_hours}h`
              : null;

    return (
        <td
            className="px-2 py-2 text-center cursor-pointer hover:bg-tertiary/50 transition-colors border border-secondary"
            onClick={onClick}
        >
            <Badge color={config.color} size="sm">
                {config.short}
                {delayLabel && entry.requirement_level !== 'required_if' && (
                    <span className="ml-1 opacity-75">{delayLabel}</span>
                )}
            </Badge>
            {(hasConditions || hasEscalation) && (
                <div className="flex items-center justify-center gap-1 mt-1">
                    {hasConditions && <span className="text-[10px]" title="Has conditions">&#x1F50D;</span>}
                    {hasEscalation && <span className="text-[10px]" title={`Escalates after ${entry.escalation_delay_hours}h`}>&#x23F1;</span>}
                </div>
            )}
        </td>
    );
}

// ============================================================================
// Edit Modal
// ============================================================================

function RequirementEditModal({
    isOpen,
    onClose,
    docTypeId,
    docTypeName,
    status,
    existing,
    presets,
}: {
    isOpen: boolean;
    onClose: () => void;
    docTypeId: string;
    docTypeName: string;
    status: string;
    existing: MatrixStatusEntry | undefined;
    presets: Record<string, ConditionPreset>;
}) {
    const createMutation = useCreateRequirement();
    const updateMutation = useUpdateRequirement();
    const deleteMutation = useDeleteRequirement();

    // Fetch schemas for this doc type to enable schema-level overrides
    const { data: schemas } = useDocumentSchemas({ document_type_id: docTypeId });

    // Unify 'required' and 'expected' into 'expected' in the UI
    // Backend 'required' = Expected with delay=0 (immediate)
    // Backend 'expected' = Expected with delay>0 (escalates)
    const uiLevel = (existing?.requirement_level === 'required' ? 'expected' : existing?.requirement_level) || 'optional';
    const uiDelay =
        existing?.requirement_level === 'required'
            ? '0'
            : existing?.escalation_delay_hours?.toString() || '';

    const [level, setLevel] = useState<RequirementLevel>(uiLevel as RequirementLevel);
    const [escalationHours, setEscalationHours] = useState<string>(uiDelay);
    const [notes, setNotes] = useState(existing?.notes || '');
    const [selectedSchemaId, setSelectedSchemaId] = useState<string>(
        existing?.document_schema_id || '__all__'
    );
    const [selectedPresets, setSelectedPresets] = useState<string[]>(() => {
        if (!existing?.conditions?.rules) return [];
        const matched: string[] = [];
        for (const [key, preset] of Object.entries(presets)) {
            const presetFields = preset.rules.map((r) => r.field).sort().join(',');
            const existingFields = existing.conditions.rules.map((r) => r.field).sort().join(',');
            if (presetFields === existingFields) {
                matched.push(key);
            }
        }
        return matched;
    });

    const schemaItems: SelectItemType[] = useMemo(() => {
        if (!schemas || schemas.length === 0) return [];
        return [
            { id: '__all__', label: 'All schemas (type-level)' },
            ...schemas.map((s) => ({
                id: s.document_schema_id,
                label: s.schema_name,
                supportingText: s.schema_code,
            })),
        ];
    }, [schemas]);

    if (!isOpen) return null;

    const handleSave = async () => {
        let conditions: ConditionGroup | null = null;
        if (level === 'required_if' && selectedPresets.length > 0) {
            const allRules = selectedPresets.flatMap((key) => presets[key]?.rules || []);
            conditions = { logic: 'OR', rules: allRules };
        }

        const schemaId = selectedSchemaId !== '__all__' ? selectedSchemaId : null;

        // Map unified UI level back to backend levels:
        // UI "expected" with delay=0 → backend "required" (immediately required)
        // UI "expected" with delay>0 → backend "expected" with escalation_delay_hours
        const delayNum = escalationHours ? parseInt(escalationHours) : 0;
        let backendLevel = level as string;
        let backendDelay: number | null = null;

        if (level === 'expected') {
            if (delayNum === 0) {
                backendLevel = 'required';
                backendDelay = null;
            } else {
                backendLevel = 'expected';
                backendDelay = delayNum;
            }
        } else if (level === 'required_if') {
            // Required If can also have escalation delay
            backendDelay = escalationHours ? parseInt(escalationHours) : null;
        }

        const payload = {
            requirement_level: backendLevel as RequirementLevel,
            conditions,
            escalation_delay_hours: backendDelay,
            notes: notes || null,
        };

        if (existing) {
            await updateMutation.mutateAsync({
                docTypeId,
                requirementId: existing.requirement_id,
                data: payload,
            });
        } else {
            await createMutation.mutateAsync({
                docTypeId,
                data: { shipment_status: status, document_schema_id: schemaId, ...payload },
            });
        }
        onClose();
    };

    const handleDelete = async () => {
        if (!existing) return;
        await deleteMutation.mutateAsync({ docTypeId, requirementId: existing.requirement_id });
        onClose();
    };

    const togglePreset = (key: string) => {
        setSelectedPresets((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-primary rounded-xl shadow-xl ring-1 ring-secondary w-full max-w-md p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-lg font-semibold text-primary">
                    {docTypeName} — {STATUS_LABELS[status] || status}
                </h3>

                {/* Level selector */}
                <Select
                    label="Requirement Level"
                    items={LEVEL_OPTIONS}
                    selectedKey={level}
                    onSelectionChange={(key) => setLevel(key as RequirementLevel)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id} textValue={item.label}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>

                {/* Schema selector (shown when doc type has schemas) */}
                {schemaItems.length > 1 && (
                    <Select
                        label="Schema Scope"
                        items={schemaItems}
                        selectedKey={selectedSchemaId}
                        onSelectionChange={(key) => setSelectedSchemaId(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                )}

                {/* Condition presets (shown for required_if) */}
                {level === 'required_if' && (
                    <div>
                        <label className="text-sm font-medium text-secondary block mb-2">Condition Presets</label>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(presets).map(([key, preset]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => togglePreset(key)}
                                    className={`px-3 py-1.5 text-xs rounded-lg ring-1 transition-colors ${
                                        selectedPresets.includes(key)
                                            ? 'bg-brand-secondary text-brand ring-brand'
                                            : 'bg-secondary text-tertiary ring-secondary hover:ring-tertiary'
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        {selectedPresets.length === 0 && (
                            <p className="text-xs text-tertiary mt-1">
                                No conditions selected — will always warn when missing
                            </p>
                        )}
                    </div>
                )}

                {/* Escalation delay (shown for expected and required_if) */}
                {(level === 'expected' || level === 'required_if') && (
                    <div>
                        <label className="text-sm font-medium text-secondary block mb-1">
                            Escalation Delay (hours)
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={escalationHours}
                            onChange={(e) => setEscalationHours(e.target.value)}
                            placeholder={level === 'expected' ? '0 = immediately required' : 'e.g. 72 (optional)'}
                            className="w-full rounded-lg bg-primary px-3.5 py-2.5 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset outline-hidden focus:ring-2 focus:ring-brand-solid transition duration-100"
                        />
                        <p className="text-xs text-tertiary mt-1">
                            {level === 'expected'
                                ? '0 = immediately required (HIGH severity). > 0 = starts as expected, escalates to REQUIRED after this many hours.'
                                : 'If set, this conditional requirement escalates to REQUIRED (HIGH severity) after this many hours, regardless of conditions.'}
                        </p>
                    </div>
                )}

                {/* Notes */}
                <div>
                    <label className="text-sm font-medium text-secondary block mb-1">Notes</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Optional notes..."
                        className="w-full rounded-lg bg-primary px-3.5 py-2.5 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset outline-hidden focus:ring-2 focus:ring-brand-solid transition duration-100 resize-none"
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2">
                    <div>
                        {existing && (
                            <Button
                                size="sm"
                                color="link-destructive"
                                onClick={handleDelete}
                                isDisabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? 'Deleting...' : 'Remove'}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" color="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            onClick={handleSave}
                            isDisabled={createMutation.isPending || updateMutation.isPending}
                        >
                            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Add Document Type Modal
// ============================================================================

function AddDocTypeModal({
    isOpen,
    onClose,
    existingDocTypeIds,
    onSelect,
}: {
    isOpen: boolean;
    onClose: () => void;
    existingDocTypeIds: Set<string>;
    onSelect: (docTypeId: string, docTypeName: string) => void;
}) {
    const { data: allDocTypes } = useDocumentTypes();

    const availableDocTypes: SelectItemType[] = useMemo(() => {
        if (!allDocTypes) return [];
        return allDocTypes
            .filter((dt) => !existingDocTypeIds.has(dt.document_type_id) && !dt.deleted_at)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((dt) => ({
                id: dt.document_type_id,
                label: dt.name,
                supportingText: dt.key,
            }));
    }, [allDocTypes, existingDocTypeIds]);

    const [selectedId, setSelectedId] = useState<string | null>(null);

    if (!isOpen) return null;

    const selectedDocType = allDocTypes?.find((dt) => dt.document_type_id === selectedId);

    const handleAdd = () => {
        if (selectedDocType) {
            onSelect(selectedDocType.document_type_id, selectedDocType.name);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-primary rounded-xl shadow-xl ring-1 ring-secondary w-full max-w-sm p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-lg font-semibold text-primary">Add Document Type</h3>
                <p className="text-sm text-tertiary">
                    Select a document type to add to the requirements matrix.
                </p>

                <Select
                    label="Document Type"
                    placeholder="Select document type..."
                    items={availableDocTypes}
                    selectedKey={selectedId}
                    onSelectionChange={(key) => setSelectedId(key as string)}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id} textValue={item.label}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>

                <div className="flex justify-end gap-2 pt-2">
                    <Button size="sm" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button size="sm" color="primary" onClick={handleAdd} isDisabled={!selectedId}>
                        Add to Matrix
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Main Page
// ============================================================================

export function RequirementsMatrixPage() {
    const { data: matrixData, isLoading } = useRequirementsMatrix();
    const { data: presetsData } = useConditionPresets();

    const deleteAllMutation = useDeleteAllRequirements();

    const [editModal, setEditModal] = useState<{
        docTypeId: string;
        docTypeName: string;
        status: string;
        existing: MatrixStatusEntry | undefined;
    } | null>(null);

    const [showAddDocType, setShowAddDocType] = useState(false);
    const [confirmDeleteRow, setConfirmDeleteRow] = useState<{ docTypeId: string; docTypeName: string } | null>(null);

    const matrix = matrixData?.matrix || [];
    const statuses = matrixData?.statuses || Object.keys(STATUS_LABELS);
    const presets = presetsData?.presets || {};

    const sortedMatrix = useMemo(
        () => [...matrix].sort((a, b) => a.document_type_name.localeCompare(b.document_type_name)),
        [matrix]
    );

    const existingDocTypeIds = useMemo(
        () => new Set(matrix.map((r) => r.document_type_id)),
        [matrix]
    );

    const handleAddDocTypeSelect = (docTypeId: string, docTypeName: string) => {
        // Open edit modal for the first status (draft) of the newly added doc type
        setEditModal({
            docTypeId,
            docTypeName,
            status: 'draft',
            existing: undefined,
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-tertiary">Loading requirements matrix...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-primary">Document Requirements Matrix</h2>
                    <p className="text-sm text-tertiary mt-1">
                        Click any cell to configure the requirement level for a document type at a specific shipment status.
                    </p>
                </div>
                <Button size="sm" color="primary" iconLeading={Plus} onClick={() => setShowAddDocType(true)}>
                    Add Doc Type
                </Button>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-1.5">
                    <Badge color="error" size="sm">EXP <span className="opacity-75">0h</span></Badge>
                    <span className="text-xs text-tertiary">Expected — immediately required</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Badge color="brand" size="sm">EXP <span className="opacity-75">48h</span></Badge>
                    <span className="text-xs text-tertiary">Expected — escalates after delay</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Badge color="warning" size="sm">REQ IF</Badge>
                    <span className="text-xs text-tertiary">Required If (conditional)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Badge color="gray" size="sm">OPT</Badge>
                    <span className="text-xs text-tertiary">Optional</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-[10px]">&#x1F50D;</span>
                    <span className="text-xs text-tertiary">Has conditions</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="text-[10px]">&#x23F1;</span>
                    <span className="text-xs text-tertiary">Has escalation timer</span>
                </div>
            </div>

            {matrix.length === 0 ? (
                <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                    <AlertCircle className="size-12 mx-auto mb-4 text-tertiary" />
                    <h3 className="text-lg font-medium text-primary mb-2">No Requirements Configured</h3>
                    <p className="text-tertiary">
                        Click "Add Doc Type" to start configuring requirements, or run the seed script.
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl ring-1 ring-secondary shadow-xs">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-secondary">
                                <th className="text-left px-3 py-2.5 font-medium text-secondary border border-secondary min-w-[200px] sticky left-0 bg-secondary z-10">
                                    Document Type
                                </th>
                                {statuses.filter((s) => s !== 'cancelled').map((status) => (
                                    <th
                                        key={status}
                                        className="px-2 py-2.5 font-medium text-secondary text-center border border-secondary min-w-[90px]"
                                    >
                                        {STATUS_LABELS[status] || status}
                                    </th>
                                ))}
                                <th className="px-2 py-2.5 font-medium text-secondary text-center border border-secondary w-[50px]" />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedMatrix.map((row) => (
                                <tr key={row.document_type_key} className="bg-primary hover:bg-tertiary/30">
                                    <td className="px-3 py-2 font-medium text-primary border border-secondary sticky left-0 bg-primary z-10">
                                        <div className="flex flex-col">
                                            <span>{row.document_type_name}</span>
                                            <span className="text-xs text-tertiary font-mono">{row.document_type_key}</span>
                                        </div>
                                    </td>
                                    {statuses.filter((s) => s !== 'cancelled').map((status) => (
                                        <MatrixCell
                                            key={status}
                                            entry={row.statuses[status]}
                                            onClick={() =>
                                                setEditModal({
                                                    docTypeId: row.document_type_id,
                                                    docTypeName: row.document_type_name,
                                                    status,
                                                    existing: row.statuses[status],
                                                })
                                            }
                                        />
                                    ))}
                                    <td className="px-2 py-2 text-center border border-secondary">
                                        <button
                                            type="button"
                                            title="Remove all requirements for this doc type"
                                            onClick={() => setConfirmDeleteRow({ docTypeId: row.document_type_id, docTypeName: row.document_type_name })}
                                            className="p-1 rounded hover:bg-error-secondary text-quaternary hover:text-error-primary transition-colors"
                                        >
                                            <Trash01 className="size-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit Modal */}
            {editModal && (
                <RequirementEditModal
                    isOpen={true}
                    onClose={() => setEditModal(null)}
                    docTypeId={editModal.docTypeId}
                    docTypeName={editModal.docTypeName}
                    status={editModal.status}
                    existing={editModal.existing}
                    presets={presets}
                />
            )}

            {/* Add Doc Type Modal */}
            {showAddDocType && (
                <AddDocTypeModal
                    isOpen={true}
                    onClose={() => setShowAddDocType(false)}
                    existingDocTypeIds={existingDocTypeIds}
                    onSelect={handleAddDocTypeSelect}
                />
            )}

            {/* Confirm Row Delete */}
            {confirmDeleteRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteRow(null)}>
                    <div
                        className="bg-primary rounded-xl shadow-xl ring-1 ring-secondary w-full max-w-sm p-6 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold text-primary">Remove Document Type</h3>
                        <p className="text-sm text-secondary">
                            This will remove <strong>all requirements</strong> for <strong>{confirmDeleteRow.docTypeName}</strong> across all statuses.
                        </p>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button size="sm" color="secondary" onClick={() => setConfirmDeleteRow(null)}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                color="link-destructive"
                                isDisabled={deleteAllMutation.isPending}
                                onClick={async () => {
                                    await deleteAllMutation.mutateAsync({ docTypeId: confirmDeleteRow.docTypeId });
                                    setConfirmDeleteRow(null);
                                }}
                            >
                                {deleteAllMutation.isPending ? 'Removing...' : 'Remove'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
